import React, { useEffect, useState, useRef } from "react";
import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import { useDesktopBreakpoint } from "web/react/hooks/use-desktop-breakpoint/use-desktop-breakpoint";
import BackButton from "web/react/components/buttons/back-button/back-button";
import styles from "./drawer.css";

function useSmoothClosing(open: boolean): [boolean, boolean] {
    // We need to delay the open prop by a frame so the isClosing state can
    // catch up with it and let the animations work their magic
    const [isOpen, setIsOpen] = useState(open);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (open) {
            setIsClosing(false);
            setIsOpen(true);
        } else if (isOpen) {
            setIsOpen(false);
            setIsClosing(true);

            setTimeout(() => setIsClosing(false), 400);
        }
    }, [isOpen, open]);

    return [isOpen, isClosing];
}

interface DrawerProps {
    open: boolean;
    title: string;
    children?: JSX.Element;
    id: string;
    onBack: () => void;
    onCancel: () => void;
    onOverlay: () => void;
}

function Drawer({
    open,
    title,
    children,
    id,
    onBack,
    onCancel,
    onOverlay,
}: DrawerProps): JSX.Element {
    let overlayRef = useRef<HTMLDivElement>(null);
    let largeLayout = useDesktopBreakpoint();
    const [isOpen, isClosing] = useSmoothClosing(open);

    useEffect(() => {
        document.body.classList.toggle("no-scroll", isOpen);
        return (): void => {
            document.body.classList.remove("no-scroll");
        };
    }, [isOpen]);

    function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>): void {
        if (event.target === overlayRef.current) {
            onOverlay();
        }
    }

    return (
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className={classnames(styles.overlay, {
                [styles.open]: isOpen,
                [styles.closing]: isClosing,
            })}
        >
            <article
                className={styles.drawer}
                role={largeLayout ? "complementary" : "dialog"}
                aria-modal={!largeLayout}
                aria-labelledby={`${id}-drawer-title`}
                aria-describedby={`${id}-drawer-children`}
                aria-hidden={!open && !largeLayout}
            >
                <div className={styles.header}>
                    <div className={styles.back}>
                        <BackButton onClick={onBack} />
                    </div>
                    <h1 className={styles.title} id={`${id}-drawer-title`}>
                        {title}
                    </h1>
                    <button className={styles.cancel} onClick={onCancel}>
                        {gettext("feed.filters.mobile_cancel_btn_text")}
                    </button>
                </div>
                <div className={styles.content} id={`${id}-drawer-children`}>
                    {children}
                </div>
            </article>
        </div>
    );
}

export default Drawer;
